export interface SpatialPoint {
  x: number;
  y: number;
}

export interface SpatialMix {
  gain: number;
  pan: number;
}

export interface SpatialMixConfig {
  baseVolume: number;
  nearRadius: number;
  farRadius: number;
  minGain: number;
  cameraRotation?: number;
}

export interface ResolveSpatialMixOptions extends SpatialMixConfig {
  listener?: SpatialPoint;
  observedSource?: SpatialPoint;
  lastSourcePosition?: SpatialPoint;
}

export interface ResolvedSpatialMix {
  mix: SpatialMix;
  sourcePosition?: SpatialPoint;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function computeFalloff(
  distance: number,
  nearRadius: number,
  farRadius: number,
  minGainValue: number,
): number {
  const near = Math.max(0, nearRadius);
  const far = Math.max(near + 1, farRadius);
  const minGain = clamp(minGainValue, 0, 1);

  if (distance <= near) return 1;
  if (distance >= far) return minGain;

  const effectiveNear = Math.max(1, near);
  const adjustedDistance = Math.max(effectiveNear, distance);
  const adjustedFar = Math.max(effectiveNear + 1, far);
  const normalized =
    Math.log(adjustedDistance / effectiveNear) / Math.log(adjustedFar / effectiveNear);
  return clamp(1 - normalized * (1 - minGain), minGain, 1);
}

export function computePointMix(
  listener: SpatialPoint,
  source: SpatialPoint,
  config: SpatialMixConfig,
): SpatialMix {
  const dx = source.x - listener.x;
  const dy = source.y - listener.y;
  const distance = Math.hypot(dx, dy);
  const rotation = Number.isFinite(config.cameraRotation) ? (config.cameraRotation ?? 0) : 0;
  const screenX = dx * Math.cos(rotation) + dy * Math.sin(rotation);
  const pan = distance < 1 ? 0 : clamp(screenX / distance, -1, 1);

  return {
    gain: clamp(
      config.baseVolume *
        computeFalloff(distance, config.nearRadius, config.farRadius, config.minGain),
      0,
      1,
    ),
    pan,
  };
}

export function quietFallbackMix(baseVolume: number, minGain: number): SpatialMix {
  return {
    gain: clamp(baseVolume * clamp(minGain, 0, 1), 0, 1),
    pan: 0,
  };
}

export function resolveSpatialMix(options: ResolveSpatialMixOptions): ResolvedSpatialMix {
  const sourcePosition = options.observedSource ?? options.lastSourcePosition;
  if (!options.listener || !sourcePosition) {
    return {
      mix: quietFallbackMix(options.baseVolume, options.minGain),
      sourcePosition,
    };
  }

  return {
    mix: computePointMix(options.listener, sourcePosition, options),
    sourcePosition,
  };
}

export function shouldWaitForUserActivation(
  contextState: "closed" | "running" | "suspended",
  hasBeenActive: boolean | undefined,
): boolean {
  return contextState === "suspended" && hasBeenActive === false;
}

const LEGACY_BUBBLE_VOICE_MODE = "default";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Mirrors the back's rule in `patch-back-source.mjs`. Room-spatial audio is the
 * platform default; only an explicit `voiceMode: "default"` opts a map out.
 *
 * Keep the two implementations behaviourally identical. A disagreement shows up
 * as a conversation bubble that appears on spawn and clears on the first step,
 * because the client would suppress the bubble on a room the server still runs
 * as legacy proximity groups (or the reverse).
 */
export function isRoomSpatialVendor(vendor: unknown): boolean {
  if (!isRecord(vendor)) {
    return true;
  }
  if (vendor.voiceMode === LEGACY_BUBBLE_VOICE_MODE) {
    return false;
  }
  const dystopia = vendor.dystopia;
  return !(isRecord(dystopia) && dystopia.voiceMode === LEGACY_BUBBLE_VOICE_MODE);
}
